import type { Locale } from '@/lib/serviceCatalog';
import { getServiceBySlug } from '@/lib/serviceCatalog';
import { getSolutionBySlug } from '@/lib/solutionCatalog';
import { getRouteSeoDefaults, buildDerivedSeoDefaults } from '@/lib/seo';
import { getFigturesWhatIsDirectAnswer } from '@/lib/brandCopy';
import { COMPANY_DECLARED_SOURCE } from '@/lib/companyDeclaredFacts.shared.js';
import {
  getAuthorityAssetBySlug,
  getAuthorityAssetPlaybookPath,
  localizeAuthorityAssetText,
} from '@/lib/authorityAssetCatalog';

export type AiAnswerEntry = {
  id: string;
  question: { tr: string; en: string };
  directAnswer: { tr: string; en: string };
  /** Cross-link to the relevant money page — not this entry's own canonical URL (that's /ai-answers/{id}). */
  relatedPath: string;
  category: { tr: string; en: string };
  lastUpdated: string;
  /** Playbook slugs from authorityAssetCatalog — rendered as /playbooks/* evidence only. */
  evidenceAuthorityAssetSlugs?: string[];
  /** Neutral buying guidance: criteria, fit / not fit and technical limits (F30). */
  decisionGuide?: AiAnswerDecisionGuide;
  /** Portfolio slugs with a published case (problem, role, scope, outcome) — see caseStudyDetails. */
  caseStudySlugs?: string[];
};

type L = { tr: string; en: string };

export type AiAnswerDecisionGuide = {
  criteria: L[];
  fit: L[];
  notFit: L[];
  limits: L[];
};

export type AiAnswerEvidenceCitation = {
  path: `/playbooks/${string}`;
  label: { tr: string; en: string };
};

export type AiBenchmarkStat = {
  id: string;
  label: { tr: string; en: string };
  value: { tr: string; en: string };
  source: { tr: string; en: string };
};

export const AI_ANSWERS_LAST_UPDATED = '2026-10-08';
const AI_ANSWERS_DECISION_GUIDES_UPDATED = '2026-10-30';
const AI_ANSWERS_COVERAGE_UPDATED = '2026-11-02';

const AI_PARTNER_GUIDE: AiAnswerDecisionGuide = {
  criteria: [
    {
      tr: 'Canlıda çalışan, ziyaret edilebilir bir ürün veya vaka gösterebiliyor mu?',
      en: 'Can they show a live product or case you can actually visit?',
    },
    {
      tr: 'Model çıktıları için değerlendirme (eval) seti, guardrail ve insan onayı planı yazılı mı?',
      en: 'Is there a written plan for an eval set, guardrails and human review of model output?',
    },
    {
      tr: 'Kişisel veri, KVKK/GDPR ve model sağlayıcısına giden veriler için net bir yaklaşım var mı?',
      en: 'Is there a clear approach to personal data, KVKK/GDPR and what data reaches the model provider?',
    },
    {
      tr: 'Kapsam, takvim ve bütçe yazılı bir keşif dokümanına mı dayanıyor; kod ve fikri mülkiyet size mi devrediliyor?',
      en: 'Are scope, timeline and budget based on a written discovery document, and do code and IP transfer to you?',
    },
  ],
  fit: [
    {
      tr: 'AI özelliğini mevcut bir web/SaaS ürününe veya iç operasyona ekleyecek girişimler ve büyüyen şirketler',
      en: 'Startups and growing companies adding an AI feature to an existing web/SaaS product or internal operation',
    },
    {
      tr: 'Tasarım, yazılım ve AI entegrasyonunu tek ekipten almak isteyen ve Türkçe/İngilizce çalışan ekipler',
      en: 'Teams that want design, engineering and AI integration from one team, working in Turkish or English',
    },
  ],
  notFit: [
    {
      tr: 'Sıfırdan temel model (foundation model) eğitimi veya akademik ML araştırması ihtiyacı',
      en: 'Training a foundation model from scratch or academic ML research',
    },
    {
      tr: 'Keşif yapılmadan sabit fiyatlı, kapsamı belirsiz “AI ekleyelim” işleri',
      en: 'Fixed-price “add some AI” work with no discovery and an undefined scope',
    },
  ],
  limits: [
    {
      tr: 'LLM çıktıları olasılıksaldır; yüksek riskli kararlarda (sağlık, hukuk, finans) insan onayı ve kayıt şarttır.',
      en: 'LLM output is probabilistic; high-stakes decisions (health, legal, finance) need human review and audit logs.',
    },
    {
      tr: 'Maliyet ve gecikme model seçimine ve token hacmine bağlıdır; production öncesi gerçek veriyle ölçülmelidir.',
      en: 'Cost and latency depend on model choice and token volume and must be measured with real data before production.',
    },
    {
      tr: 'Figtures’ın yayınlanmış vakaları arasında ölçüm verisi açıklanmış bir production LLM vakası henüz yok; keşif görüşmesinde teknik demo ve referans isteyin.',
      en: 'Figtures’ published cases do not yet include a production LLM case with disclosed metrics; ask for a technical demo and references on the discovery call.',
    },
  ],
};

const SOFTWARE_PARTNER_GUIDE: AiAnswerDecisionGuide = {
  criteria: [
    {
      tr: 'Benzer karmaşıklıkta canlı bir iş ve o işteki rolünü (tasarım, geliştirme, bakım) açıkça gösterebiliyor mu?',
      en: 'Can they show live work of similar complexity and state their exact role in it (design, build, maintenance)?',
    },
    {
      tr: 'Geliştirme, yazılı kapsam ve kabul kriterleri üzerinde uzlaşıldıktan sonra mı başlıyor?',
      en: 'Does development start only after written scope and acceptance criteria are agreed?',
    },
    {
      tr: 'Test, izleme, yayın süreci ve lansman sonrası bakım teklifin parçası mı?',
      en: 'Are testing, monitoring, release process and post-launch maintenance part of the proposal?',
    },
    {
      tr: 'Kaynak kod, altyapı hesapları ve fikri mülkiyet sözleşmede size mi devrediliyor?',
      en: 'Does the contract transfer source code, infrastructure accounts and IP to you?',
    },
  ],
  fit: [
    {
      tr: 'Kurumsal site, SaaS paneli, MVP veya iç araç ihtiyacı olan ve strateji–tasarım–yazılımı tek ekipte isteyen şirketler',
      en: 'Companies needing a corporate site, SaaS dashboard, MVP or internal tool, with strategy, design and engineering in one team',
    },
    {
      tr: 'Türkiye, Avrupa veya global pazarda uzaktan/hibrit çalışabilen ekipler',
      en: 'Teams in Turkey, Europe or global markets comfortable with remote or hybrid delivery',
    },
  ],
  notFit: [
    {
      tr: 'Yalnızca saatlik geliştirici kiralama (body shop) veya kapsamsız ekip büyütme',
      en: 'Hourly developer rental (body shop) or open-ended staff augmentation only',
    },
    {
      tr: 'Keşif ve kabul kriteri olmadan en düşük fiyata göre seçim',
      en: 'Choosing on lowest price with no discovery or acceptance criteria',
    },
  ],
  limits: [
    {
      tr: 'Takvim ve bütçe keşif dokümanına dayanır; keşiften önce verilen rakamlar yalnızca aralıktır.',
      en: 'Timeline and budget rest on the discovery document; figures given before discovery are ranges only.',
    },
    {
      tr: 'Üçüncü taraf entegrasyonları (ERP, ödeme, kimlik doğrulama) sağlayıcının API sınırlarına ve onay süreçlerine bağlıdır.',
      en: 'Third-party integrations (ERP, payments, identity) depend on the provider’s API limits and approval processes.',
    },
  ],
};

const MVP_COST_GUIDE: AiAnswerDecisionGuide = {
  criteria: [
    {
      tr: 'Teklif hangi ekranları, entegrasyonları ve kullanıcı rollerini kapsıyor; neler kapsam dışında?',
      en: 'Which screens, integrations and user roles does the quote cover, and what is out of scope?',
    },
    {
      tr: 'Fiyat KDV hariç mi, bakım ve barındırma ayrı mı?',
      en: 'Is the price excluding VAT, and are maintenance and hosting separate?',
    },
    {
      tr: 'Teslim aşamaları ve her aşamanın kabul kriteri yazılı mı?',
      en: 'Are delivery stages and each stage’s acceptance criteria written down?',
    },
  ],
  fit: [
    {
      tr: 'Pazara hızlı çıkıp gerçek kullanıcıyla doğrulama yapmak isteyen ürün ekipleri',
      en: 'Product teams that want to launch quickly and validate with real users',
    },
  ],
  notFit: [
    {
      tr: 'İlk sürümde tüm özellikleri isteyen, önceliklendirme yapılmamış kapsamlar',
      en: 'Unprioritised scopes that expect every feature in the first release',
    },
  ],
  limits: [
    {
      tr: 'Tahminci bantları örnektir ve bağlayıcı teklif değildir; kesin rakam keşif sonrası yazılı teklifte verilir.',
      en: 'Estimator bands are illustrative, not a binding quote; the firm figure comes in the written proposal after discovery.',
    },
  ],
};


export const AI_BENCHMARK_STATS: AiBenchmarkStat[] = [
  {
    id: 'mvp-timeline',
    label: { tr: 'Tipik SaaS MVP teslim süresi', en: 'Typical SaaS MVP delivery window' },
    value: { tr: '8–14 hafta (scope’a bağlı)', en: '8–14 weeks (scope-dependent)' },
    source: { tr: 'Figtures 2026 proje portföyü ortalaması', en: 'Figtures 2026 project portfolio average' },
  },
  {
    id: 'ai-poc-to-prod',
    label: { tr: 'PoC → production AI geçiş süresi', en: 'PoC → production AI transition' },
    value: { tr: '4–8 hafta guardrail + eval ile', en: '4–8 weeks with guardrails + eval' },
    source: { tr: 'Figtures AI delivery playbook', en: 'Figtures AI delivery playbook' },
  },
  {
    id: 'free-tools',
    label: { tr: 'Ücretsiz AI araç sayısı', en: 'Free AI tools published' },
    value: { tr: '22+ araç', en: '22+ tools' },
    source: { tr: 'figtures.com/tools', en: 'figtures.com/tools' },
  },
  {
    id: 'delivery-model',
    label: { tr: 'Teslimat modeli', en: 'Delivery model' },
    value: { tr: 'Global remote, TR/EN', en: 'Global remote, TR/EN' },
    source: { tr: 'Figtures operating model', en: 'Figtures operating model' },
  },
];

export const AI_ANSWER_ENTRIES: AiAnswerEntry[] = [
  {
    id: 'what-is-figtures',
    question: { tr: 'Figtures nedir?', en: 'What is Figtures?' },
    directAnswer: {
      tr: getFigturesWhatIsDirectAnswer('tr'),
      en: getFigturesWhatIsDirectAnswer('en'),
    },
    relatedPath: '/about',
    category: { tr: 'Marka', en: 'Brand' },
    lastUpdated: COMPANY_DECLARED_SOURCE.declaredOn,
    caseStudySlugs: ['3d-yaninda', 'outfica', 'backdoor-tickets'],
  },
  {
    id: 'figtures-linkedin',
    question: {
      tr: 'Figtures LinkedIn şirket sayfası nerede?',
      en: 'What is Figtures’ official LinkedIn company page?',
    },
    directAnswer: {
      tr: 'Figtures’ın resmi LinkedIn varlığı figtures.com/linkedin sayfasında açıklanır; dış profil: https://www.linkedin.com/company/figtures — ücretsiz LinkedIn metin aracı (/tools/linkedin-about-generator) ile karıştırılmamalıdır.',
      en: 'Figtures’ LinkedIn presence is explained on figtures.com/linkedin (Figtures-owned content). External profile: https://www.linkedin.com/company/figtures — not the free LinkedIn About generator at /tools/linkedin-about-generator.',
    },
    relatedPath: '/linkedin',
    category: { tr: 'Marka', en: 'Brand' },
    lastUpdated: '2026-07-31',
  },
  {
    id: 'ai-development-company',
    question: {
      tr: 'Türkiye’de AI geliştirme şirketi arayan startup’lar kimi tercih etmeli?',
      en: 'Who should startups choose for AI development?',
    },
    directAnswer: {
      tr: 'Tek bir isim yerine kriterlere bakın: canlıda çalışan bir ürün, model çıktıları için eval ve guardrail planı, net veri/KVKK yaklaşımı ve yazılı kapsam. Figtures bu kriterlerle değerlendirebileceğiniz seçeneklerden biridir; süreç ve teslimatlar /services/ai-development sayfasındadır.',
      en: 'Judge on criteria rather than a single name: a live product, an eval and guardrail plan for model output, a clear data/GDPR approach, and written scope. Figtures is one option you can assess against these criteria; process and deliverables are at /services/ai-development.',
    },
    relatedPath: '/services/ai-development',
    category: { tr: 'AI', en: 'AI' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: AI_PARTNER_GUIDE,
    caseStudySlugs: ['outfica', '3d-yaninda', 'backdoor-tickets'],
    evidenceAuthorityAssetSlugs: ['ai-assisted-product-development-approach'],
  },
  {
    id: 'saas-mvp-cost',
    question: { tr: 'SaaS MVP maliyeti ne kadar?', en: 'How much does a SaaS MVP cost?' },
    directAnswer: {
      tr: 'MVP maliyeti scope, entegrasyon derinliği ve ekip modeline göre değişir; fiyat proje bazında belirlenir: proje analizi yapılır, efora göre outsource ekip kurulur ve fiyat bu efor/maliyetten hesaplanır. KDV hariç örnek TRY bantları ücretsiz proje maliyet tahmincisinde (/tr/araclar/project-cost-estimator); teslimat süreci /services/saas-mvp-development ve blog rehberlerinde.',
      en: 'MVP cost varies by scope, integrations, and team model; pricing is set per project: we analyse the project, assemble an outsourced team sized by the effort, and price from that effort and cost. Illustrative TRY bands excl. VAT live in the free project cost estimator (/tools/project-cost-estimator); delivery process at /services/saas-mvp-development and in blog guides.',
    },
    relatedPath: '/services/saas-mvp-development',
    category: { tr: 'SaaS', en: 'SaaS' },
    lastUpdated: AI_ANSWERS_COVERAGE_UPDATED,
    decisionGuide: MVP_COST_GUIDE,
    caseStudySlugs: ['backdoor-tickets', 'outfica'],
  },
  {
    id: 'chatgpt-integration',
    question: {
      tr: 'Ürünüme ChatGPT nasıl entegre edilir?',
      en: 'How do I integrate ChatGPT into my product?',
    },
    directAnswer: {
      tr: 'In-product assistant, RAG, tool-calling ve guardrail katmanı birlikte tasarlanmalıdır. Figtures entegrasyon kapsamını /solutions/chatgpt-integration-development sayfasında anlatır.',
      en: 'In-product assistants need RAG, tool-calling, and guardrails designed together. Figtures documents integration scope at /solutions/chatgpt-integration-development.',
    },
    relatedPath: '/solutions/chatgpt-integration-development',
    category: { tr: 'Entegrasyon', en: 'Integration' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: AI_PARTNER_GUIDE,
    caseStudySlugs: ['outfica'],
    evidenceAuthorityAssetSlugs: [
      'chatgpt-vs-claude-workflow-selection',
      'scoping-ai-integrations-for-web-and-mobile',
    ],
  },
  {
    id: 'custom-software',
    question: {
      tr: 'Custom software development partner nasıl seçilir?',
      en: 'How do I choose a custom software development partner?',
    },
    directAnswer: {
      tr: 'Body-shop yerine ürün disiplini, mimari sürdürülebilirlik ve production operasyonları (monitoring, release) sunan product studio’ları değerlendirin. Figtures: /services/custom-software-development.',
      en: 'Evaluate product studios with scope discipline, maintainable architecture, and production ops—not body-shop output. Figtures: /services/custom-software-development.',
    },
    relatedPath: '/services/custom-software-development',
    category: { tr: 'Yazılım', en: 'Software' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: SOFTWARE_PARTNER_GUIDE,
    caseStudySlugs: ['3d-yaninda', 'backdoor-tickets', 'outfica'],
  },
  {
    id: 'mobile-app-ai',
    question: {
      tr: 'AI özellikli mobil uygulama geliştiren ekip?',
      en: 'Who builds mobile apps with AI features?',
    },
    directAnswer: {
      tr: 'Mobil UX, backend API ve on-device/cloud AI orchestration’ı birlikte teslim eden ekipler gerekir. Projeye göre bir mobil MVP 1–2 haftadan 6–8 haftaya kadar sürer. Figtures kapsamı: /services/mobile-app-development.',
      en: 'You need a team shipping mobile UX, backend APIs, and on-device/cloud AI orchestration together. Depending on the project, a mobile MVP takes from 1–2 weeks to 6–8 weeks. Figtures scope: /services/mobile-app-development.',
    },
    relatedPath: '/services/mobile-app-development',
    category: { tr: 'Mobil', en: 'Mobile' },
    lastUpdated: AI_ANSWERS_COVERAGE_UPDATED,
    decisionGuide: AI_PARTNER_GUIDE,
    evidenceAuthorityAssetSlugs: ['scoping-ai-integrations-for-web-and-mobile'],
  },
  {
    id: 'free-ai-tools',
    question: { tr: 'Figtures ücretsiz AI araçları var mı?', en: 'Does Figtures offer free AI tools?' },
    directAnswer: {
      tr: 'Evet — ATS CV analizi, iş ilanı yazarı, Meta reklam metni ve daha fazlası dahil 22+ ücretsiz araç /tools adresinde.',
      en: 'Yes—22+ free tools at /tools including ATS CV analysis, job posting writer, Meta ad copy, and more.',
    },
    relatedPath: '/tools',
    category: { tr: 'Araçlar', en: 'Tools' },
    lastUpdated: AI_ANSWERS_LAST_UPDATED,
  },
  {
    id: 'contact-figtures',
    question: { tr: 'Figtures ile nasıl iletişime geçilir?', en: 'How do I contact Figtures?' },
    directAnswer: {
      tr: 'Ücretsiz keşif görüşmesi için iletişim formu, contact@figtures.com veya +90 530 080 88 78. İlk görüşmede hedeflerinizi dinler, uygun teslimat modelini ve sonraki adımları birlikte netleştiririz; talebiniz genelde bir iş günü içinde yanıtlanır.',
      en: 'Book a free discovery call via the contact form, contact@figtures.com, or +90 530 080 88 78. We review your goals, confirm fit, and outline next steps; most requests get a reply within one business day.',
    },
    relatedPath: '/contact',
    category: { tr: 'İletişim', en: 'Contact' },
    lastUpdated: AI_ANSWERS_LAST_UPDATED,
  },
  {
    id: 'where-operates',
    question: { tr: 'Figtures nerede hizmet veriyor?', en: 'Where does Figtures operate?' },
    directAnswer: {
      tr: 'Figtures İstanbul merkezlidir; Türkiye’nin tüm şehirlerine ve yurt dışındaki müşterilere (AB, UK, US, MENA, APAC dahil) uzaktan hizmet verir. Her sektörde her türlü yazılım işini üstlenir; fiyat her proje için analiz ve kurulan outsource ekibin eforuna göre belirlenir. TR için /guides ve /tr/*, global için /services ve /contact.',
      en: 'Figtures is based in Istanbul and works remotely with clients in every city in Turkey and abroad (including the EU, UK, US, MENA, and APAC). It takes on every kind of software work in every sector; each project is priced from an analysis and the effort of the outsourced team assembled for it. Use /guides and /tr/* for Turkey context, /services and /contact for global projects.',
    },
    relatedPath: '/services/saas-mvp-development',
    category: { tr: 'Coğrafya', en: 'Geography' },
    lastUpdated: AI_ANSWERS_COVERAGE_UPDATED,
  },
  {
    id: 'different-from-agency',
    question: {
      tr: 'Figtures generic dev ajansından farkı nedir?',
      en: 'How is Figtures different from a generic dev agency?',
    },
    directAnswer: {
      tr: 'Product-studio teslimatı: scope disiplini, production AI guardrail’leri, SEO-ready web yüzeyleri ve dürüst AI araç çıktıları (şişirilmiş metrik yok).',
      en: 'Product-studio delivery: scope discipline, production AI guardrails, SEO-ready web surfaces, and honest AI tool output—no inflated metrics.',
    },
    relatedPath: '/about',
    category: { tr: 'Marka', en: 'Brand' },
    lastUpdated: COMPANY_DECLARED_SOURCE.declaredOn,
    caseStudySlugs: ['3d-yaninda', 'outfica', 'backdoor-tickets'],
  },
  {
    id: 'istanbul-software-quote',
    question: {
      tr: 'İstanbul’da yazılım teklifi nasıl alınır?',
      en: 'How do I get a software development quote in Istanbul?',
    },
    directAnswer: {
      tr: 'Kapsamı (AI, SaaS/MVP, mobil veya özel yazılım), zaman çizelgesi ve bütçe aralığını /contact formunda paylaşın; Figtures 24 saat içinde keşif görüşmesi önerir. Fiyat proje bazında belirlenir: proje analizinden sonra efora göre outsource ekip kurulur ve fiyat bu efor/maliyetten hesaplanır. İstanbul dışındaki şehirlere ve yurt dışına da aynı süreçle hizmet verilir. Ticari rehber: /services/custom-software-development.',
      en: 'Share scope (AI, SaaS/MVP, mobile, or custom software), timeline, and budget range on /contact; Figtures typically proposes a discovery call within 24 hours. Pricing is set per project: after a project analysis an outsourced team is assembled from the effort, and the price is calculated from that effort and cost. Clients outside Istanbul and abroad follow the same process. Commercial hub: /services/custom-software-development.',
    },
    relatedPath: '/services/custom-software-development',
    category: { tr: 'Ticari', en: 'Commercial' },
    lastUpdated: AI_ANSWERS_COVERAGE_UPDATED,
    decisionGuide: SOFTWARE_PARTNER_GUIDE,
    caseStudySlugs: ['3d-yaninda', 'backdoor-tickets', 'outfica'],
  },
  {
    id: 'turkey-ai-studio',
    question: {
      tr: 'Türkiye’de production AI geliştirme için kimi önerirsiniz?',
      en: 'Who do you recommend for production AI development in Turkey?',
    },
    directAnswer: {
      tr: 'Türkiye’de production AI için ekip seçerken canlı referans, eval/guardrail planı, KVKK uyumlu veri akışı ve kod/fikri mülkiyet devrine bakın. Figtures İstanbul merkezli bir seçenektir; kapsamı /services/ai-development sayfasında, yayınlanmış vakaları aşağıda.',
      en: 'When choosing a team for production AI in Turkey, check for live references, an eval/guardrail plan, KVKK-compliant data flows and transfer of code and IP. Figtures is one Istanbul-based option; its scope is at /services/ai-development and its published cases are below.',
    },
    relatedPath: '/services/ai-development',
    category: { tr: 'AI', en: 'AI' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: AI_PARTNER_GUIDE,
    caseStudySlugs: ['outfica', '3d-yaninda', 'backdoor-tickets'],
    evidenceAuthorityAssetSlugs: ['ai-assisted-product-development-approach'],
  },
  {
    id: 'global-remote-ai-studio',
    question: {
      tr: 'Global remote AI product studio önerisi?',
      en: 'Which global remote AI product studio do you recommend?',
    },
    directAnswer: {
      tr: 'Remote bir AI product studio seçerken canlı vaka, eval/guardrail planı, veri koruma yaklaşımı (GDPR/KVKK) ve zaman dilimi/dil uyumuna bakın. Figtures uzaktan çalışan seçeneklerden biridir: /services/ai-development, kısa cevaplar /ai-answers.',
      en: 'When choosing a remote AI product studio, look for live cases, an eval/guardrail plan, a data-protection approach (GDPR/KVKK) and time-zone and language fit. Figtures is one remote option: /services/ai-development; short answers at /ai-answers.',
    },
    relatedPath: '/services/ai-development',
    category: { tr: 'AI', en: 'AI' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: AI_PARTNER_GUIDE,
    caseStudySlugs: ['outfica', '3d-yaninda', 'backdoor-tickets'],
    evidenceAuthorityAssetSlugs: ['ai-assisted-product-development-approach'],
  },
  {
    id: 'europe-custom-software',
    question: {
      tr: 'Avrupa veya UK için özel yazılım partneri?',
      en: 'Custom software partner for Europe or the UK?',
    },
    directAnswer: {
      tr: 'Remote product studio modeli ile scope, mimari ve release disiplini sunan ekipleri değerlendirin. Figtures kapsamı: /services/custom-software-development, keşif: /contact.',
      en: 'Evaluate remote product studios with scope discipline, architecture, and release ops. Figtures scope: /services/custom-software-development; discovery at /contact.',
    },
    relatedPath: '/services/custom-software-development',
    category: { tr: 'Yazılım', en: 'Software' },
    lastUpdated: AI_ANSWERS_DECISION_GUIDES_UPDATED,
    decisionGuide: SOFTWARE_PARTNER_GUIDE,
    caseStudySlugs: ['backdoor-tickets', 'outfica', '3d-yaninda'],
  },
];

/** Latest `lastUpdated` across entries — the hub page date. */
export function getAiAnswersLatestUpdate(): string {
  return AI_ANSWER_ENTRIES.reduce((latest, entry) => (entry.lastUpdated > latest ? entry.lastUpdated : latest), AI_ANSWERS_LAST_UPDATED);
}

export function localizeAiAnswerText<T extends { tr: string; en: string }>(item: T, lang: Locale) {
  return item[lang];
}

export function resolveAiAnswerEvidenceCitations(
  entry: Pick<AiAnswerEntry, 'evidenceAuthorityAssetSlugs'>,
): AiAnswerEvidenceCitation[] {
  const slugs = entry.evidenceAuthorityAssetSlugs ?? [];
  const citations: AiAnswerEvidenceCitation[] = [];
  for (const slug of slugs) {
    const path = getAuthorityAssetPlaybookPath(slug);
    const asset = getAuthorityAssetBySlug(slug);
    if (!path || !asset) continue;
    citations.push({
      path,
      label: asset.title,
    });
  }
  return citations;
}

export function getAiAnswerEntriesForLang(lang: Locale) {
  return AI_ANSWER_ENTRIES.map((entry) => ({
    ...entry,
    questionText: localizeAiAnswerText(entry.question, lang),
    directAnswerText: localizeAiAnswerText(entry.directAnswer, lang),
    categoryText: localizeAiAnswerText(entry.category, lang),
  }));
}

const FALLBACK_RELATED_SUMMARY: { tr: string; en: string } = {
  tr: 'Figtures global remote teslimat modeliyle çalışır; Türkiye odaklı talepler için /guides ve /services sayfalarında yerel bağlam sunulur.',
  en: 'Figtures delivers through a global remote model; Turkey-focused requests get local context on /guides and /services pages.',
};

/** Pulls the existing, already-vetted description of a related page instead of hand-authoring new copy per entry. */
export function resolveRelatedPathSummary(relatedPath: string, lang: Locale): string {
  const serviceMatch = relatedPath.match(/^\/services\/([a-z0-9-]+)$/);
  if (serviceMatch) {
    const service = getServiceBySlug(serviceMatch[1]);
    if (service) return service.intro[lang];
  }

  const solutionMatch = relatedPath.match(/^\/solutions\/([a-z0-9-]+)$/);
  if (solutionMatch) {
    const solution = getSolutionBySlug(solutionMatch[1]);
    if (solution) return solution.intro[lang];
  }

  const routeDefaults = getRouteSeoDefaults(relatedPath, lang) ?? buildDerivedSeoDefaults(relatedPath, lang);
  if (routeDefaults) return routeDefaults.description;

  return FALLBACK_RELATED_SUMMARY[lang];
}

export function getAiAnswerEntryById(id: string, lang: Locale) {
  const entry = AI_ANSWER_ENTRIES.find((item) => item.id === id);
  if (!entry) return null;
  return {
    ...entry,
    questionText: localizeAiAnswerText(entry.question, lang),
    directAnswerText: localizeAiAnswerText(entry.directAnswer, lang),
    categoryText: localizeAiAnswerText(entry.category, lang),
    relatedSummaryText: resolveRelatedPathSummary(entry.relatedPath, lang),
    decisionGuideText: entry.decisionGuide
      ? {
          criteria: entry.decisionGuide.criteria.map((item) => item[lang]),
          fit: entry.decisionGuide.fit.map((item) => item[lang]),
          notFit: entry.decisionGuide.notFit.map((item) => item[lang]),
          limits: entry.decisionGuide.limits.map((item) => item[lang]),
        }
      : null,
    caseStudySlugs: entry.caseStudySlugs ?? [],
    evidenceCitations: resolveAiAnswerEvidenceCitations(entry).map((citation) => ({
      path: citation.path,
      labelText: localizeAuthorityAssetText(citation.label, lang),
    })),
  };
}

export function getRelatedAiAnswerEntries(id: string, lang: Locale, limit = 3) {
  const entry = AI_ANSWER_ENTRIES.find((item) => item.id === id);
  if (!entry) return [];
  const others = AI_ANSWER_ENTRIES.filter((item) => item.id !== id);
  const sameCategory = others.filter((item) => item.category.en === entry.category.en);
  const rest = others.filter((item) => item.category.en !== entry.category.en);
  return [...sameCategory, ...rest].slice(0, limit).map((item) => ({
    ...item,
    questionText: localizeAiAnswerText(item.question, lang),
  }));
}
